import { Box, Button, Grid, Paper, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import * as services from "../../services/saleng";
import { formatNumber } from "../../utils/formatNumber";

const ProductPricing = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const [productsResponse, adjustmentsResponse] = await Promise.all([
        services.getProducts(),
        services.getProductAdjustments(),
      ]);
      const adjustmentByProductId = new Map(
        (adjustmentsResponse.data.data || []).map((item) => [
          item.productId,
          item,
        ])
      );

      setRows(
        (productsResponse.data.data || []).map((product, index) => {
          const adjustmentItem = adjustmentByProductId.get(product.id);
          const adjustment = Number(adjustmentItem?.adjustment || 0);
          const basePrice = Number(product.price || 0);

          return {
            id: product.id,
            no: index + 1,
            title: product.title || "-",
            category: product.category?.name || "-",
            subCategory: product.subCategory?.name || "-",
            basePrice,
            targetPrice: basePrice + adjustment,
            savedTargetPrice: basePrice + adjustment,
            hasAdjustment: Boolean(adjustmentItem),
            isDirty: false,
          };
        })
      );
    } catch (error) {
      console.error("Unable to load product pricing:", error);
      toast.error("โหลดข้อมูลราคาไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const processRowUpdate = (newRow, oldRow) => {
    const targetPrice = Number(newRow.targetPrice);
    if (!Number.isFinite(targetPrice) || targetPrice < 0) {
      toast.error("กรุณาระบุราคารับซื้อเป็นตัวเลขตั้งแต่ 0 ขึ้นไป");
      return oldRow;
    }

    const updatedRow = {
      ...newRow,
      targetPrice,
      isDirty: targetPrice !== oldRow.savedTargetPrice,
    };

    setRows((currentRows) =>
      currentRows.map((row) => (row.id === newRow.id ? updatedRow : row))
    );
    return updatedRow;
  };

  const savePricing = async () => {
    const dirtyRows = rows.filter((row) => row.isDirty);
    if (!dirtyRows.length) return;

    setSaving(true);
    const savedIds = new Set();

    for (const row of dirtyRows) {
      const adjustment = row.targetPrice - row.basePrice;
      try {
        if (row.hasAdjustment) {
          await services.updateProductAdjustment(row.id, { adjustment });
        } else {
          await services.createProductAdjustment({
            productId: row.id,
            adjustment,
          });
        }
        savedIds.add(row.id);
      } catch (error) {
        console.error(`Unable to save product ${row.id} pricing:`, error);
      }
    }

    setRows((currentRows) =>
      currentRows.map((row) =>
        savedIds.has(row.id)
          ? {
              ...row,
              hasAdjustment: true,
              savedTargetPrice: row.targetPrice,
              isDirty: false,
            }
          : row
      )
    );
    setSaving(false);

    const failedCount = dirtyRows.length - savedIds.size;
    if (failedCount) {
      toast.error(`บันทึกไม่สำเร็จ ${failedCount} รายการ`);
    }
    if (savedIds.size) {
      toast.success(`บันทึกราคาสำเร็จ ${savedIds.size} รายการ`);
    }
  };

  const columns = useMemo(
    () => [
      { field: "no", headerName: "ลำดับ", width: 75 },
      { field: "title", headerName: "ชื่อสินค้า", flex: 1.5, minWidth: 180 },
      { field: "category", headerName: "หมวดหมู่", flex: 1, minWidth: 130 },
      {
        field: "subCategory",
        headerName: "หมวดหมู่ย่อย",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "basePrice",
        headerName: "ราคาพื้นฐาน",
        type: "number",
        flex: 1,
        minWidth: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatNumber(value || 0),
      },
      {
        field: "targetPrice",
        headerName: "ราคารับซื้อที่ต้องการ",
        renderCell: (params) => (
          <Tooltip title="ดับเบิลคลิกที่ราคาเพื่อแก้ไข">
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.5,
                cursor: "pointer",
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1 }}>
                {formatNumber(params.value || 0)}
              </Typography>
              <EditIcon
                fontSize="small"
                color="action"
                sx={{ display: "block" }}
              />
            </Box>
          </Tooltip>
        ),
        type: "number",
        editable: true,
        flex: 1.3,
        minWidth: 190,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatNumber(value || 0),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" />
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="flex-start"
        sx={{ my: 1 }}
      >
        <Grid item xs={12} sm={9} md={10}>
          <Typography component="h1" variant="h5" fontWeight={600}>
            จัดการราคาสินค้า
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            ดับเบิลคลิกที่ราคารับซื้อเพื่อแก้ไข แล้วกดบันทึกเมื่อตรวจสอบครบแล้ว
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <Button
            fullWidth
            size="small"
            variant="contained"
            onClick={savePricing}
            disabled={loading || saving || !rows.some((row) => row.isDirty)}
          >
            {saving ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </Grid>
      </Grid>
      <Paper sx={{ mt: 2 }}>
        <div style={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            editMode="cell"
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={() => {}}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            disableRowSelectionOnClick
          />
        </div>
      </Paper>
    </Box>
  );
};

export default ProductPricing;
